import React from 'react'
import {useIntl} from 'react-intl'
import {ControllerRenderProps} from 'react-hook-form'
import {POSTCODE_REGEXP} from '../../utils/constants'
import {CheckPostalCodeFormFields, CreateFields} from '../../types/forms'
import useDeliveryBooking from '../use-delivery-booking'

export function formatPostcode(postcode: string) {
  // 1. Clean and uppercase the input string
  const cleaned = postcode.replace(/[^A-Z0-9]/gi, '').toUpperCase()
  const length = cleaned.length

  // 2. Too short to need a space
  if (length <= 2) {
    return cleaned
  }

  // 3. For 5+ characters, find the inward code transition from the back
  if (length >= 5) {
    const inwardMatch = cleaned.match(/(\d[A-Z]{1,2})$/)
    if (inwardMatch && inwardMatch.index !== undefined) {
      const splitPos = inwardMatch.index
      return cleaned.slice(0, splitPos) + ' ' + cleaned.slice(splitPos)
    }
    // If it ends in a number (e.g., LS112), the final number belongs to the inward code
    if (/^\d$/.test(cleaned[length - 1])) {
      return cleaned.slice(0, -1) + ' ' + cleaned.slice(-1)
    }
    return cleaned
  }

  // 4. For 3 or 4 characters, handle live structural spacing
  if (length === 4) {
    // Only split if there are 3 numbers in a row (e.g., M114 -> M11 4)
    // Avoid splitting 2 letters + 2 numbers (e.g., LS11 stays LS11)
    const threeDigitMatch = cleaned.match(/^([A-Z]{1,2}\d{2})(\d)$/)
    if (threeDigitMatch) {
      return threeDigitMatch[1] + ' ' + threeDigitMatch[2]
    }

    // Also split if a 1-letter area has 2 numbers and a trailing number (e.g., M114 -> M11 4)
    const singleLetterThreeDigit = cleaned.match(/^([A-Z]\d)(\d{2})$/)
    if (singleLetterThreeDigit) {
      return singleLetterThreeDigit[1] + ' ' + singleLetterThreeDigit[2]
    }
  }

  return cleaned
}

export const useCheckPostcodeFields: CreateFields<
  CheckPostalCodeFormFields,
  {setPostCodeAvailability?: ReturnType<typeof useDeliveryBooking>['setPostCodeAvailability']}
> = ({
  form: {
    control,
    formState: {errors}
  },
  setPostCodeAvailability
}) => {
  const {formatMessage} = useIntl()

  const fields = {
    postcode: {
      name: `postcode` as const,
      label: 'Enter your postcode',
      type: 'text' as const,
      defaultValue: '',
      rules: {
        required: formatMessage({defaultMessage: 'This field is required.'}),
        validate: (postcode: string) => {
          const formatted = formatPostcode(postcode)
          return POSTCODE_REGEXP.test(formatted) || 'Please specify a valid postal code'
        }
      },
      inputProps: ({onChange}: Pick<ControllerRenderProps, 'onChange'>) => ({
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          onChange(e.target.value)
          if (setPostCodeAvailability) {
            setPostCodeAvailability(undefined)
          }
        }
      }),
      error: errors[`postcode`],
      control
    }
  }

  return fields
}
